import React, { useState, useEffect } from 'react';
import { Tab, Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import CustomerDetailsContainer from '../../containers/CustomerDetailsContainer/CustomerDetailsContainer';
import OrderStatusFinal from '../../pages/OrderStatusFinal/OrderStatusFinal';
import StatusHistory from '../../components/StatusHistory/StatusHistory';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import AdminPortalSideBar from '../../containers/AdminSideBarContainer/AdminSideBarContainer';
import { Container, TabContent, StyledTabs, BodyWrapper, ContentWrapper, TabPanelWrapper } from './OrderManagementTabs.styles';
import { getOrderById } from '../../api/api';
import { useAuth } from '../../contexts/AuthContext';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HistoryIcon from '@mui/icons-material/History';

/**
 * @param {boolean} embedded - When true, render only tab strip + panels (no shell). Use inside
 *   OrderManagement, which already provides TopNavbar + AdminPortalSideBar.
 */
export default function OrderManagementTabs({
  order: orderProp,
  defaultTab = 0,
  readOnly: readOnlyProp,
  embedded = false,
}) {
  const { hasPermission } = useAuth();
  const readOnly = readOnlyProp !== undefined ? readOnlyProp : !hasPermission('orders.manage');
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [order, setOrder] = useState(orderProp || null);
  const params = useParams();

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (orderProp !== undefined && orderProp !== null) {
      setOrder(orderProp);
    }
  }, [orderProp]);

  useEffect(() => {
    if (!orderProp && params?.orderNumber) {
      (async () => {
        const data = await getOrderById(params.orderNumber);
        setOrder(data);
      })();
    }
  }, [orderProp, params?.orderNumber]);

  const handleChange = (_, newValue) => setActiveTab(newValue);

  const tabHeader = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
      <StyledTabs value={activeTab} onChange={handleChange} textColor="primary" indicatorColor="primary">
        <Tab icon={<CompareArrowsIcon />} iconPosition="start" label="Order Status" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Status History" />
        <Tab icon={<SupervisedUserCircleIcon />} iconPosition="start" label="Customer Details" />
      </StyledTabs>
      {order?.order_number && (
        <Typography variant="body1" sx={{ mr: 3, fontWeight: 600, color: '#002F87', fontSize: '0.95rem' }}>
          Order #{order.order_number}
        </Typography>
      )}
    </Box>
  );

  const tabPanels = (
    <TabContent>
      {activeTab === 0 && (
        <TabPanelWrapper>
          <OrderStatusFinal embedded order={order} onOrderChanged={setOrder} readOnly={readOnly} />
        </TabPanelWrapper>
      )}

      {activeTab === 1 && (
        <TabPanelWrapper>
          <StatusHistory orderId={order?.id} />
        </TabPanelWrapper>
      )}

      {activeTab === 2 && (
        <TabPanelWrapper>
          <CustomerDetailsContainer order={order} />
        </TabPanelWrapper>
      )}
    </TabContent>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {tabHeader}
        {tabPanels}
      </Box>
    );
  }

  return (
    <Container>
      <TopNavbar onTabChange={() => {}} />
      <BodyWrapper>
        <AdminPortalSideBar />
        <ContentWrapper>
          {tabHeader}
          {tabPanels}
        </ContentWrapper>
      </BodyWrapper>
    </Container>
  );
}
